'use client';

/**
 * `GET /platform/paywall-cta` — what the plan wall offers on the screens
 * that read it. Cached for ten minutes; a failure is `plans` (today's wall),
 * never an error on the page.
 */
import { useQuery } from '@tanstack/react-query';

import { useApi } from '@/lib/api';

import { DEFAULT_PAYWALL_CTA, normalisePaywallCta, type PaywallCta } from './planWall';

export const PAYWALL_CTA_QUERY_KEY = ['platform', 'paywall-cta'] as const;

export interface PaywallCtaState {
  cta: PaywallCta;
  /** True until the first answer (or failure) — the caller may hold the CTA
   *  back rather than flash the plans link and swap it. */
  isPending: boolean;
}

export function usePaywallCta(): PaywallCtaState {
  const api = useApi();
  const q = useQuery<PaywallCta, Error>({
    queryKey: PAYWALL_CTA_QUERY_KEY,
    queryFn: async ({ signal }) =>
      normalisePaywallCta(await api<unknown>('/platform/paywall-cta', { signal })),
    staleTime: 10 * 60_000,
    retry: false,
    meta: { suppressGlobalRedirect: true },
  });
  return {
    cta: q.data ?? DEFAULT_PAYWALL_CTA,
    isPending: q.isPending && !q.isError,
  };
}
